/**
 * Timeline30 — the 30-day card-charge timeline (spec §6.5). Not a value chart: a horizontal
 * LTR axis from "היום" on the LEFT to today+30 on the right, with one marker per upcoming
 * charge (filled = סופי, hollow = נצבר, dashed ring = the connection expired). Colliding
 * markers stack upwards with a leader line. On mobile the caller renders the list twin.
 */

import { fmtDM, fmtMoney } from '../format.ts';
import { ChartTooltip, useTooltip, type TipContent } from './Tooltip.tsx';
import { stackCollisions } from './chart-utils.ts';
import { useChartWidth } from './useChartWidth.ts';

export interface TimelineItem {
  date: string;
  /** agorot */
  amount: number;
  label: string;
  final: boolean;
  expired: boolean;
  aria: string;
  tip: TipContent;
}

export function Timeline30({ asOf, items, big }: { asOf: string; items: TimelineItem[]; big?: boolean }) {
  const [ref, width] = useChartWidth(420);
  const tooltip = useTooltip();
  const height = big ? 118 : 104;
  const margin = width < 480 ? 30 : 40;
  const y = big ? 68 : 60;
  const days = 30;
  const px = (day: number) => margin + (day / days) * (width - margin * 2);

  const visible = items
    .map((item) => ({ item, day: Math.round((Date.parse(item.date) - Date.parse(asOf)) / 86400000) }))
    .filter(({ day }) => day >= 0 && day <= days);
  const lifts = stackCollisions(visible.map(({ day }) => px(day)));

  return (
    <div className="timeline timeline-desk" role="group" aria-label="ציר זמן 30 יום — חיובי אשראי" ref={ref}>
      <svg viewBox={`0 0 ${width} ${height}`}>
        <line x1={margin} x2={width - margin} y1={y} y2={y} stroke="var(--border-strong)" strokeWidth="2" strokeLinecap="round" />
        {[7, 14, 21, 28].map((week) => (
          <line key={week} x1={px(week).toFixed(1)} x2={px(week).toFixed(1)} y1={y - 4} y2={y + 4} stroke="var(--border-strong)" />
        ))}
        <path d={`M${margin} ${y - 9} l-5 -8 h10 z`} fill="var(--text-2)" />
        <text x={margin} y={y - 30} textAnchor="end" className="strong bidi">
          היום · {fmtDM(asOf)}
        </text>
        <text x={width - margin} y={y - 30} textAnchor="end">
          {fmtDM(new Date(Date.parse(asOf) + days * 86400000).toISOString().slice(0, 10))}
        </text>
        {visible.map(({ item, day }, index) => {
          const x = px(day);
          const lift = lifts[index] ?? 0;
          const cy = y - lift;
          const handlers = tooltip.bind(item.tip);
          return (
            <g key={`${item.label}-${item.date}`} tabIndex={0} role="img" aria-label={item.aria} {...handlers}>
              {lift ? <line x1={x.toFixed(1)} x2={x.toFixed(1)} y1={cy} y2={y} stroke="var(--border-strong)" /> : null}
              <circle cx={x.toFixed(1)} cy={cy} r="14" className="hit" />
              <circle
                cx={x.toFixed(1)}
                cy={cy}
                r="6"
                className={item.final ? 's1' : 'surf'}
                stroke="var(--series-1)"
                strokeWidth="2"
                strokeDasharray={item.expired ? '2 2' : undefined}
              />
              <text x={x.toFixed(1)} y={cy - 13} textAnchor="middle" className="strong">
                {fmtDM(item.date)}
              </text>
              <text x={x.toFixed(1)} y={y + 22} textAnchor="middle" className="t1">
                {item.label} · {fmtMoney(item.amount)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="legend" aria-label="מקרא">
        <span>
          <LegendDot filled />
          סופי
        </span>
        <span>
          <LegendDot />
          נצבר עד כה
        </span>
        <span>
          <LegendDot dashed />
          חיבור פג תוקף
        </span>
      </div>
      <ChartTooltip state={tooltip.node} />
    </div>
  );
}

function LegendDot({ filled, dashed }: { filled?: boolean; dashed?: boolean }) {
  return (
    <svg viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5" className={filled ? 's1' : 'surf'} stroke="var(--series-1)" strokeWidth="2" strokeDasharray={dashed ? '2 2' : undefined} />
    </svg>
  );
}
